import React, { useState } from 'react';
import { Icon } from './ui/Icon.jsx';
import { useVendors, usePendingVendorProducts, useReviewVendorProduct, useManageVendor } from '../hooks/useVendors.js';
import { ViewAsVendorButton } from './admin/ViewAsVendorButton.jsx';
import { COLORS, input, button, cardBox, VENDOR_STATUS } from '../lib/theme.js';
import { fmt } from '../lib/utils.js';

// ─── ADMIN: VENDORS TAB ───────────────────────────────────────────────────────
// Sedra stays in control: vendors apply, staff approve; vendor products land
// as pending and only go live in the catalogue after staff review here.
export default function Vendors({ lang, onViewAsVendor }) {
  const ar = lang === 'ar';
  const { data: vendors = [],  isLoading }  = useVendors();
  const { data: pendingProducts = [] }      = usePendingVendorProducts();
  const reviewProduct = useReviewVendorProduct();
  const manageVendor  = useManageVendor();

  const [approveTarget, setApproveTarget] = useState(null);   // vendor being approved
  const [loginForm, setLoginForm]         = useState({ email: '', password: '' });
  const [msg, setMsg]                     = useState('');
  const [err, setErr]                     = useState('');

  const vendorName = (v) => (ar && v.business_name_ar) ? v.business_name_ar : v.business_name_en;
  const byId = Object.fromEntries(vendors.map(v => [v.id, v]));

  const pending  = vendors.filter(v => v.status === 'pending');
  const active   = vendors.filter(v => v.status === 'active');
  const inactive = vendors.filter(v => v.status === 'suspended' || v.status === 'rejected');

  const runAction = async (payload, okMsg) => {
    setErr(''); setMsg('');
    try {
      await manageVendor.mutateAsync(payload);
      setMsg(okMsg);
      setApproveTarget(null);
      setLoginForm({ email: '', password: '' });
    } catch (e) { setErr(e.message); }
  };

  const chip = (status) => {
    const s = VENDOR_STATUS[status] || VENDOR_STATUS.pending;
    return (
      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, color: s.color, background: s.bg }}>
        {ar ? s.label_ar : s.label_en}
      </span>
    );
  };

  const vendorRow = (v, actions) => (
    <div key={v.id} style={{ ...cardBox, marginBottom: 10, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: '1 1 220px', minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: 15, color: COLORS.brandDark, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="Store" size={15} /> {vendorName(v)} {chip(v.status)}
        </div>
        <div style={{ fontSize: 12.5, color: COLORS.textMuted, marginTop: 4, lineHeight: 1.7, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          {v.contact_name && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="User" size={12} /> {v.contact_name} ·</span>}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="Phone" size={12} /> {v.phone}</span>
          {v.email && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>· <Icon name="Mail" size={12} /> {v.email}</span>}
          {v.tax_id && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>· <Icon name="FileText" size={12} /> {v.tax_id}</span>}
        </div>
        {v.notes && <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2, display: 'flex', alignItems: 'flex-start', gap: 4 }}><Icon name="FileText" size={12} style={{ flexShrink: 0, marginTop: 2 }} /> {v.notes}</div>}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{actions}</div>
    </div>
  );

  if (isLoading) return <div style={{ padding: 40, color: COLORS.textMuted }}>{ar ? 'جارٍ التحميل…' : 'Loading…'}</div>;

  return (
    <div style={{ padding: 24, maxWidth: 980, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 6px', color: COLORS.brandDark, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="Store" size={20} /> {ar ? 'البائعون — سوق سدرة هوم' : 'Vendors — Sedra Home Marketplace'}
      </h2>
      <p style={{ margin: '0 0 22px', fontSize: 13, color: COLORS.textMuted }}>
        {ar
          ? 'أصحاب الأعمال ينشرون منتجاتهم تحت مظلة سدرة هوم — كل منتج يحتاج موافقتكم قبل ظهوره في الكتالوج.'
          : 'Business owners sell under the Sedra Home umbrella — every product needs your approval before it appears in the catalogue.'}
      </p>

      {msg && <div style={{ padding: '10px 16px', background: COLORS.successBg, color: COLORS.success, borderRadius: 8, marginBottom: 14, fontSize: 13, fontWeight: 700 }}>{msg}</div>}
      {err && <div style={{ padding: '10px 16px', background: COLORS.dangerBg, color: COLORS.danger, borderRadius: 8, marginBottom: 14, fontSize: 13, fontWeight: 700 }}>{err}</div>}

      {/* ── Product approval queue ── */}
      <h3 style={{ margin: '0 0 10px', fontSize: 15, color: COLORS.warnDark, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon name="Clock" size={14} /> {ar ? `منتجات بانتظار المراجعة (${pendingProducts.length})` : `Products awaiting review (${pendingProducts.length})`}
      </h3>
      {pendingProducts.length === 0 && (
        <div style={{ ...cardBox, marginBottom: 22, color: COLORS.textMuted, fontSize: 13 }}>
          {ar ? 'لا توجد منتجات بانتظار المراجعة' : 'No products awaiting review'}
        </div>
      )}
      {pendingProducts.map(p => (
        <div key={p.id} style={{ ...cardBox, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ width: 54, height: 54, borderRadius: 8, background: COLORS.imageWell, overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.textMuted }}>
            {p.image ? <img src={p.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Icon name="Package" size={24} />}
          </div>
          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.brandDarker }}>{ar && p.name_ar ? p.name_ar : p.name_en}</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
              {byId[p.vendor_id] ? `${vendorName(byId[p.vendor_id])} · ` : ''}{p.sku} · {fmt(p.price)} · {ar ? 'مخزون' : 'stock'} {p.stock}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...button(COLORS.success), display: 'inline-flex', alignItems: 'center', gap: 6 }} disabled={reviewProduct.isPending}
              onClick={() => reviewProduct.mutate({ id: p.id, decision: 'approved' })}>
              <Icon name="Check" size={13} /> {ar ? 'موافقة' : 'Approve'}
            </button>
            <button style={{ ...button(COLORS.danger), display: 'inline-flex', alignItems: 'center', gap: 6 }} disabled={reviewProduct.isPending}
              onClick={() => reviewProduct.mutate({ id: p.id, decision: 'rejected' })}>
              <Icon name="X" size={13} /> {ar ? 'رفض' : 'Reject'}
            </button>
          </div>
        </div>
      ))}

      {/* ── Vendor applications ── */}
      <h3 style={{ margin: '26px 0 10px', fontSize: 15, color: COLORS.brandDark, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon name="ClipboardList" size={14} /> {ar ? `طلبات انضمام (${pending.length})` : `Vendor applications (${pending.length})`}
      </h3>
      {pending.length === 0 && (
        <div style={{ ...cardBox, marginBottom: 22, color: COLORS.textMuted, fontSize: 13 }}>
          {ar ? 'لا توجد طلبات جديدة.' : 'No new applications.'}
        </div>
      )}
      {pending.map(v => vendorRow(v, (
        <>
          <button style={{ ...button(COLORS.brand), display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => { setApproveTarget(v); setLoginForm({ email: v.email || '', password: '' }); setErr(''); setMsg(''); }}>
            <Icon name="Check" size={13} /> {ar ? 'قبول وإنشاء حساب' : 'Approve & create login'}
          </button>
          <button style={button(COLORS.textMuted)} disabled={manageVendor.isPending}
            onClick={() => runAction({ action: 'reject', vendor_id: v.id }, ar ? 'تم رفض الطلب' : 'Application rejected')}>
            {ar ? 'رفض' : 'Reject'}
          </button>
        </>
      )))}

      {/* Approve modal (inline card) */}
      {approveTarget && (
        <div style={{ ...cardBox, border: `2px solid ${COLORS.brand}`, marginBottom: 22 }}>
          <div style={{ fontWeight: 800, color: COLORS.brandDark, marginBottom: 10 }}>
            {ar ? `إنشاء حساب دخول للبائع: ${vendorName(approveTarget)}` : `Create login for vendor: ${vendorName(approveTarget)}`}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input style={{ ...input, flex: '1 1 220px' }} type="email" placeholder={ar ? 'البريد الإلكتروني للدخول' : 'Login email'}
              value={loginForm.email} onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))} />
            <input style={{ ...input, flex: '1 1 180px' }} type="text" placeholder={ar ? 'كلمة مرور مؤقتة (٦+ أحرف)' : 'Temp password (6+ chars)'}
              value={loginForm.password} onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))} />
            <button style={button(COLORS.brand)} disabled={manageVendor.isPending || !loginForm.email || loginForm.password.length < 6}
              onClick={() => runAction(
                { action: 'approve', vendor_id: approveTarget.id, email: loginForm.email, password: loginForm.password },
                ar ? 'تم تفعيل البائع — أرسل له بيانات الدخول' : 'Vendor activated — send them the login details'
              )}>
              {manageVendor.isPending ? '…' : (ar ? 'تفعيل' : 'Activate')}
            </button>
            <button style={button(COLORS.textMuted)} onClick={() => setApproveTarget(null)}>{ar ? 'إلغاء' : 'Cancel'}</button>
          </div>
        </div>
      )}

      {/* ── Active vendors ── */}
      <h3 style={{ margin: '26px 0 10px', fontSize: 15, color: COLORS.brandDark, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon name="CheckCircle2" size={14} /> {ar ? `بائعون نشطون (${active.length})` : `Active vendors (${active.length})`}
      </h3>
      {active.length === 0 && (
        <div style={{ ...cardBox, marginBottom: 22, color: COLORS.textMuted, fontSize: 13 }}>
          {ar ? 'لا يوجد بائعون نشطون بعد. شارك رابط /sell مع أصحاب الأعمال.' : 'No active vendors yet. Share the /sell link with business owners.'}
        </div>
      )}
      {active.map(v => vendorRow(v, (
        <>
          <ViewAsVendorButton vendor={v} lang={lang} onClick={onViewAsVendor} />
          <button style={{ ...button(COLORS.warn), display: 'inline-flex', alignItems: 'center', gap: 6 }} disabled={manageVendor.isPending}
            onClick={() => runAction({ action: 'suspend', vendor_id: v.id }, ar ? 'تم إيقاف البائع' : 'Vendor suspended')}>
            <Icon name="Ban" size={13} /> {ar ? 'إيقاف' : 'Suspend'}
          </button>
        </>
      )))}

      {/* ── Suspended / rejected ── */}
      {inactive.length > 0 && (
        <>
          <h3 style={{ margin: '26px 0 10px', fontSize: 15, color: COLORS.textMuted, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="Ban" size={14} /> {ar ? 'موقوفون / مرفوضون' : 'Suspended / rejected'}
          </h3>
          {inactive.map(v => vendorRow(v, (
            v.status === 'suspended'
              ? <button style={{ ...button(COLORS.success), display: 'inline-flex', alignItems: 'center', gap: 6 }} disabled={manageVendor.isPending}
                  onClick={() => runAction({ action: 'reactivate', vendor_id: v.id }, ar ? 'تمت إعادة التفعيل' : 'Vendor reactivated')}>
                  <Icon name="RotateCcw" size={13} /> {ar ? 'إعادة تفعيل' : 'Reactivate'}
                </button>
              : null
          )))}
        </>
      )}
    </div>
  );
}
